import { grommet } from 'grommet';
import { deepMerge } from 'grommet/utils';
import { defaultTheme } from 'speed-js-react-web';
import { css as styledCss } from 'styled-components';

const redFlash = `
    -webkit-animation-name: flash; /* Safari 4.0 - 8.0 */
    -webkit-animation-duration: 1.5s; /* Safari 4.0 - 8.0 */
    animation-name: flash;
    animation-duration: 1.5s;
    
    /* Safari 4.0 - 8.0 */
    @-webkit-keyframes flash {
        0%   {background-color: none;}
        50%  {background-color: red;}
        100%  {background-color: none;}
    }
    
    /* Standard syntax */
    @keyframes flash {
        0%   {background-color: none;}
        50%  {background-color: red;}
        100%  {background-color: none;}
    }
`;

const MEDIUM_SIZE = '14px';

export const CONTEXT_NAVBAR_HEIGHT = '56px';
export const HEADER_HEIGHT = '60px';
export const EDGE_SIZE_SMALL = '10px';
export const AVATAR_BORDER_RADIUS = '0px';
const INPUT_FONT_SIZE = '13px';

const menuButtonBoxCss = `height: 32px;
                              & > div {
                              padding: 4px;
                          }`;

const theme = deepMerge(grommet, defaultTheme, {
    global: {
        colors: {
            brand: '#0074d9',
            'brand-grey': '#6e7482',
            'brand-dark-grey': '#616774',
            focus: 0,
            'accent-1': '#0080b4',
            text: {
                dark: '#6e7482',
                light: '#6e7482',
            },
            backgroundColor: '#f8f8f8',
            navigation: '#ecedef',
            buttonBorder: '#D3D5D9',
        },
        input: {
            weight: 400,
            font: {
                height: '2px',
                size: 'medium',
            },
            padding: {
                right: '32px',
            },
        },
        control: {
            border: {
                radius: '0px',
            },
        },
        font: {
            family: 'Open Sans, sans-serif',
        },
        drop: {
            extend: () => `
                font-size: ${INPUT_FONT_SIZE};
            `,
        },
        edgeSize: {
            small: EDGE_SIZE_SMALL,
        },
        hover: {
            color: 'none',
        },
    },
    medium: {
        size: MEDIUM_SIZE,
    },
    small: {
        size: '10px',
    },
    text: {
        xsmall: {
            size: '8px',
            height: '15px',
            maxWidth: '288px',
        },
        small: {
            size: '10px',
            height: '16px',
            maxWidth: '336px',
        },
        medium: {
            size: MEDIUM_SIZE,
            height: '18px',
            maxWidth: '432px',
        },
        large: {
            size: '22px',
            height: '28px',
            maxWidth: '528px',
        },
        xlarge: {
            size: '26px',
            height: '32px',
            maxWidth: '624px',
        },
        xxlarge: {
            size: '34px',
            height: '40px',
            maxWidth: '816px',
        },
    },
    // (props.focus ? 'box-shadow: none;' : undefined),
    textInput: {
        extend: (props) => {
            let css = `padding: 6px 32px 6px 6px; font-size: ${INPUT_FONT_SIZE}; line-height: 18px;`;
            if (props.size === 'small' || props.focus === '') {
                css = `
                    width: 80px;
                    height: 33px;
                    text-align: center;
                `;
            }
            if (props.errors && props.touched) {
                css += `
                    ${redFlash} 
                `;
            }
            return css;
        },
        placeholder: {
            extend: (props) => `font-size: ${INPUT_FONT_SIZE}`,
        },
    },
    textArea: {
        extend: (props) => {
            let css = `height: 32px; padding: 6px; font-size: ${INPUT_FONT_SIZE}; line-height: 18px;`;
            if (props.errors && props.touched) {
                css += `
                    ${redFlash}
                `;
            }
            return css;
        },
    },
    maskedInput: {
        extend: (props) => {
            let css = 'height: 32px; line-height: 18px;';
            if (props.errors && props.touched) {
                css += `
                    ${redFlash}
                `;
            }
            return css;
        },
    },
    accordion: {
        border: null,
        icons: {
            color: '#6e7482',
        },
    },
    heading: {
        margin: 0,
        extend: (props) => `margin: ${props.theme.heading.margin}`,
        level: {
            1: {
                font: {},
                small: {
                    size: '40px',
                    height: '46px',
                },
                medium: {
                    size: '56px',
                    height: '62px',
                },
                large: {
                    size: '88px',
                    height: '94px',
                },
                xlarge: {
                    size: '120px',
                    height: '126px',
                },
            },
        },
    },
    tab: {
        color: {
            dark: 'text.dark',
            light: 'text.light',
        },
        active: {
            color: {
                dark: 'accent-1',
                light: 'brand',
            },
        },
        hover: {
            color: {
                dark: 'text.dark',
                light: 'text.light',
            },
        },
        border: {
            color: {
                dark: '#6e7482',
                light: '#6e7482',
            },
            active: {
                color: {
                    dark: 'accent-1',
                    light: 'brand',
                },
            },
            hover: {
                color: {
                    dark: '#6e7482',
                    light: '#6e7482',
                },
            },
        },
    },
    tabs: {
        panel: {
            extend: () => 'overflow: auto',
        },
    },
    button: {
        border: {
            radius: '0px',
        },
        extend: (props) => {
            let style = '';
            if (props.primary) {
                style = 'color: #ffffff;';
            }
            if (props.size === 'small') {
                return `
                    ${style}
                    padding: 3px;
                    border-radius: 0px;
                `;
            }

            return style;
        },
    },
    radioButton: {
        size: '18px',
    },
    anchor: {
        hover: {
            textDecoration: {

            },
        },
    },
    layer: {
        zIndex: 1101,
    },
    drop: {
        border: {
            radius: '0px',
        },
        margin: {
            top: '20px',
        },
    },
    menu: {
        icons: {
            color: 'brand-grey',
        },
        extend: () => styledCss`
            border: solid 1px '#bfc1c8';
            border-radius: 0px;

            > div > button {
                ${menuButtonBoxCss}
            }

            > div > div > button {
                ${menuButtonBoxCss}
            }
        `,
    },
    select: {
        options: {
            container: {
                height: '32px',
                padding: '4px',
            },
        },
        control: {
            height: '32px',
            padding: 0,
            radius: '0px',
            borderRadius: '0px',
        },
        container: {
            height: '32px',
        },
        indicatorsContainer: {
            indicatorContainer: {
                padding: '2px',
            },
        },
    },
    customSelect: {
        default: {
            valueContainer: {
                // backgroundColor: '#fff',
                fontSize: INPUT_FONT_SIZE,
                padding: '0 8px',
                minHeight: '30px',
                maxHeight: '200px',
                overflow: 'auto',
            },
            menu: {
                marginTop: '0px',
            },
            menuList: {
                fontSize: 'small',
                border: '2px solid #2684FF',
                borderRadius: '0px',
            },
            menuPortal: {
                zIndex: 20,
                marginBottom: '10px',
            },
            indicatorsContainer: {
                height: '30px',
            },
            control: {
                minHeight: '32px',
                alignContent: 'center',
                border: '1px solid rgba(0, 0, 0, 0.33)',
                borderRadius: '0px',
                radius: '0px',
            },
            container: {
                minHeight: '32px',
                padding: 0,
            },
            option: {
                minHeight: '32px',
                lineHeight: '16px',
            },
            multiValue: {
                height: '26px',
                lineHeight: '20px',
            },
            input: {
                margin: 0,
            },
        },
        hasErrors: {
            valueContainer: {
                animationName: 'flash',
                animationDuration: '1.5s',
                '@keyframes flash': {
                    '0%': { backgroundColor: 'none' },
                    '50%': { backgroundColor: 'red' },
                    '100%': { backgroundColor: 'none' },
                },
            },
            control: {
                minHeight: '32px',
            },
        },
    },
    listSelect: {
        default: {
            valueContainer: {
                // backgroundColor: '#fff',
                fontSize: INPUT_FONT_SIZE,
                padding: '0 8px',
                minHeight: '30px',
                maxHeight: '200px',
                overflow: 'auto',
            },
            menuList: {
                fontSize: 'small',
            },
            menuPortal: {
                zIndex: 20,
            },
            indicatorsContainer: {
                height: '30px',
            },
            control: {
                minHeight: '32px',
                alignContent: 'center',
                borderRadius: '0px',
                radius: '0px',
            },
            container: {
                minHeight: '32px',
            },
            option: {
                minHeight: '32px',
                lineHeight: '16px',
            },
            multiValue: {
                height: '26px',
                lineHeight: '20px',
            },
            input: {
                margin: 0,
            },
        },
        hasErrors: {
            valueContainer: {
                animationName: 'flash',
                animationDuration: '1.5s',
                '@keyframes flash': {
                    '0%': { backgroundColor: 'none' },
                    '50%': { backgroundColor: 'red' },
                    '100%': { backgroundColor: 'none' },
                },
            },
        },
    },
    calendar: {
        button: {
            extend: () => 'padding: 0px',
        },
    },
    table: {
        button: {
            border: {
                width: '1px',
            },
            extend: () => `
                padding: 4px;
            `,
        },
        icon: {
            size: {
                medium: '19px',
            },
        },
        actions: {
            button: {
                border: {
                    radius: '0px',
                },
                extend: () => `
                    padding: 7px;
                `,
                size: 'medium',
            },
            global: {
                edgeSize: {
                    small: '10px',
                },
            },
            icon: {
                size: {
                    medium: '16px',
                },
            },
        },
        select: {
            options: {
                text: {
                    size: INPUT_FONT_SIZE,
                },
            },
        },
    },
    formField: {
        label: {
            margin: {
                horizontal: '0px',
            },
        },
        border: {
            color: 'transparent',
        },
        margin: {
            bottom: 'none',
        },
    },
    background: '#f8f8f8',
    pagination: {
        select: {
            icons: {
                margin: '0',
            },
            control: {
                extend: () => 'min-width: 75px;',
                borderRadius: '0px',
            },
            options: {
                container: {
                    width: '75px',
                },
            },
        },
        button: {
            border: {
                color: '#6e7482',
            },
        },
    },
    calling: {
        green: {
            default: '#e2e6ac',
            active: '#d0d873',
            text: '#457f24',
        },
        red: {
            default: '#ffb2b2',
            active: '#ff6666',
            text: '#990000',
        },
    },
    quickView: {
        button: {
            extend: () => `
                    padding: 0;
                `,
        },
    },
});


export default theme;
